import React, { useState, useEffect, useRef } from "react";

function AnimatedScore({ value, startValue = null, duration = 1500, className = "" }) {
  const [displayValue, setDisplayValue] = useState(startValue !== null ? startValue : value);
  const previousValueRef = useRef(startValue !== null ? startValue : value);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    const currentValue = value;
    const previousValue = previousValueRef.current;

    if (currentValue !== previousValue) {
      // Cancel any ongoing animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      const startVal = startValue !== null ? startValue : previousValue;
      const endVal = currentValue;
      const startTime = Date.now();

      const animate = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Linear animation (no easing)
        const currentDisplayValue = Math.floor(startVal + (endVal - startVal) * progress);
        setDisplayValue(currentDisplayValue);

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate);
        } else {
          setDisplayValue(endVal);
          previousValueRef.current = endVal;
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);
      previousValueRef.current = currentValue;
    }
  }, [value, startValue, duration]);

  // Color cycle: sf-blue, sf-yellow, sf-green, black
  const colors = [
    "var(--sf-blue, #063da6)",
    "var(--sf-yellow, #f5d531)",
    "var(--sf-green, #4caf50)",
    "black",
  ];

  // Get color for the entire number (all digits have the same color)
  const getNumberColor = (currentDisplayValue, finalValue) => {
    const isAnimationComplete = displayValue === value;
    
    // Always end with black when animation is complete
    if (isAnimationComplete) {
      return colors[3]; // black
    }
    
    // During animation, cycle through colors based on current value
    const colorIndex = Math.floor(currentDisplayValue) % colors.length;
    return colors[colorIndex];
  };

  const valueString = String(displayValue);
  const digits = valueString.split("");
  const numberColor = getNumberColor(displayValue, value);

  return (
    <span 
      className={className} 
      style={{ 
        display: "inline-block",
        color: numberColor,
        transition: "color 0.2s ease",
      }}
    >
      {valueString}
    </span>
  );
}

export default AnimatedScore;

